import React, { useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Flex, Text, toast } from "@jsq/ds/core";
import { FormTextInput, SubmitButton } from "@jsq/ds/forms";
import { useCancelPaymentBatchForTreasuryAdminMutation } from "~/graphql/supergraphHooks";
import { getErrorMessage } from "~/utils/errors";
import { toDbId } from "~/utils/graphql-helpers";
import * as styles from "./styles_vanilla.css";

interface FormValues {
  paymentBatchExternalId: string;
}

const validationSchema = Yup.object().shape({
  paymentBatchExternalId: Yup.string().required("Payment batch external ID is required"),
});

const initialValues: FormValues = {
  paymentBatchExternalId: "",
};

const CancelPaymentBatch: React.FC = () => {
  const [submittedPaymentBatch, setSubmittedPaymentBatch] = useState<any>(null);

  const [cancelPaymentBatch, { loading }] = useCancelPaymentBatchForTreasuryAdminMutation({
    onCompleted: (data) => {
      if (data?.cancelPaymentBatchForTreasuryAdmin) {
        setSubmittedPaymentBatch(data.cancelPaymentBatchForTreasuryAdmin);
        toast.success("Payment batch successfully canceled");
      }
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const handleSubmit = async (values: FormValues) => {
    await cancelPaymentBatch({
      variables: {
        cancelPaymentBatchForTreasuryAdminInput: {
          paymentBatchId: values.paymentBatchExternalId,
        },
      },
    });
  };

  return (
    <Flex
      data-testid="cancel-payment-batch-root"
      flexDirection="column"
      className={styles.container}
    >
      <div className={styles.header}>
        <Text variant="regular-xxxl" className={styles.pageTitle}>
          Cancel Payment Batch
        </Text>
      </div>
      <div className={styles.contentArea} data-testid="cancel-payment-batch-content">
        <div className={styles.formSection}>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            <Form>
              <div className={styles.formFields}>
                <FormTextInput
                  name="paymentBatchExternalId"
                  label="Payment Batch External ID"
                  placeholder="Enter payment batch external ID"
                  required
                />
              </div>
              <div className={styles.submitSection}>
                <SubmitButton isLoading={loading}>Cancel Payment Batch</SubmitButton>
              </div>
            </Form>
          </Formik>
        </div>

        {submittedPaymentBatch && (
          <div className={styles.resultSection}>
            <Text variant="regular-xl" className={styles.resultTitle}>
              Payment Batch Canceled Successfully
            </Text>
            <div className={styles.paymentBatchDetails}>
              <Text variant="regular-md" className={styles.detailLabel}>
                Payment Batch ID:{" "}
                <span className={styles.detailValue}>{submittedPaymentBatch.id}</span>
              </Text>
              <Text variant="regular-md" className={styles.detailLabel}>
                Status:{" "}
                <span className={styles.detailValue}>{submittedPaymentBatch.status}</span>
              </Text>
              <Text variant="regular-md" className={styles.detailLabel}>
                Object ID:{" "}
                <span className={styles.detailValue}>
                  {toDbId(submittedPaymentBatch.objectId)}
                </span>
              </Text>
            </div>
          </div>
        )}
      </div>
    </Flex>
  );
};

export default CancelPaymentBatch;
export { CancelPaymentBatch };
